import { transparentize } from "polished";
import React from "react";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";

/** A springy ease that overshoots a little, so the knob "lands". */
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const SIZES = {
  normal: { width: 40, height: 22, knob: 18 },
  small: { width: 32, height: 18, knob: 14 },
};

type TrackProps = SpaceProps & {
  checked: boolean;
  sizing: "normal" | "small";
  neutral: boolean;
};

const Track = styled.button<TrackProps>`
  --w: ${(p) => SIZES[p.sizing].width}px;
  --h: ${(p) => SIZES[p.sizing].height}px;
  --k: ${(p) => SIZES[p.sizing].knob}px;
  --gap: calc((var(--h) - var(--k)) / 2);

  position: relative;
  flex: 0 0 auto;
  width: var(--w);
  height: var(--h);
  padding: 0;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  background-color: ${(p) =>
    p.checked && !p.neutral ? p.theme.colors.primary : p.theme.colors.grey};
  transition:
    background-color 0.2s ease,
    box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  ${space};

  /* The knob. */
  &::after {
    content: "";
    position: absolute;
    top: var(--gap);
    left: var(--gap);
    width: var(--k);
    height: var(--k);
    border-radius: 999px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transform: translateX(
      ${(p) => (p.checked ? "calc(var(--w) - var(--k) - 2 * var(--gap))" : "0")}
    );
    /* No press effect on the knob: growing it on mousedown flashes a
       false start before the real slide. */
    transition: transform 0.35s ${SPRING};
  }

  &:hover:not(:disabled) {
    background-color: ${(p) =>
      p.checked && !p.neutral ? p.theme.colors.primary : p.theme.colors.grey1};
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px
      ${(p) => transparentize(0.7, p.theme.colors.primary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

export type SwitchProps = SpaceProps & {
  value: boolean;
  onChange: (newValue: boolean) => void;
  disabled?: boolean;
  size?: "normal" | "small";
  /** Keep the track grey when on (for an either/or choice, not on/off). */
  neutral?: boolean;
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};

/** An on/off switch: a real button with role="switch", so keyboards and screen readers can use it. */
export const Switch = ({
  value,
  onChange,
  disabled = false,
  size = "normal",
  neutral = false,
  ...rest
}: SwitchProps) => (
  <Track
    type="button"
    role="switch"
    aria-checked={value}
    checked={value}
    sizing={size}
    neutral={neutral}
    disabled={disabled}
    onClick={() => onChange(!value)}
    {...rest}
  />
);
